import { useState } from "react";
import { api, currentRole } from "../lib/api";
import { useFetch } from "../lib/useFetch";

interface Summary { total: number; byCategory: Record<string, number>; averageRating: number; ratedCount: number }
interface Feedback { id: number; category: string; rating: number | null; comment: string; source: string }

const CATEGORIES = ["compliment", "suggestion", "concern", "service_quality", "other"];

export default function FeedbackSection() {
  const manager = ["owner", "coordinator"].includes(currentRole() ?? "");
  const summary = useFetch<Summary>("/v1/feedback/summary");
  const list = useFetch<Feedback[]>("/v1/feedback");
  const [form, setForm] = useState({ category: "compliment", rating: "5", comment: "" });
  const [done, setDone] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.comment.trim()) return;
    await api("/v1/feedback", { method: "POST", body: JSON.stringify({
      category: form.category, rating: form.rating ? Number(form.rating) : undefined, comment: form.comment.trim(),
    }) });
    setForm({ category: "compliment", rating: "5", comment: "" });
    setDone(true); setTimeout(() => setDone(false), 2000);
    if (manager) { summary.reload(); list.reload(); }
  }

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-medium text-ink">Feedback</h2>
      {manager && summary.data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Total" value={summary.data.total} />
          <Stat label="Avg rating" value={summary.data.averageRating || "—"} />
          <Stat label="Compliments" value={summary.data.byCategory.compliment ?? 0} />
          <Stat label="Concerns" value={summary.data.byCategory.concern ?? 0} />
        </div>
      )}
      <form onSubmit={submit} className="card p-4 grid grid-cols-2 sm:grid-cols-6 gap-3 items-end">
        <label className="block"><span className="label">Category</span>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select></label>
        <label className="block"><span className="label">Rating</span>
          <select value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="input">
            <option value="">—</option>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
          </select></label>
        <label className="block sm:col-span-3"><span className="label">Comment</span>
          <input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} className="input" /></label>
        <button className="btn-dark">Submit{done ? "ed ✓" : ""}</button>
      </form>
      {manager && (list.data ?? []).map((f) => (
        <div key={f.id} className="card p-3 text-sm flex items-center gap-2">
          <span className="chip bg-slate-100 text-body capitalize">{f.category.replace(/_/g, " ")}</span>
          {f.rating && <span className="text-amber-600">{"★".repeat(f.rating)}</span>}
          <span className="text-body">{f.comment}</span>
        </div>
      ))}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="card p-4">
      <p className="text-2xl font-semibold text-ink">{value}</p>
      <p className="text-xs text-mut">{label}</p>
    </div>
  );
}
